import { sortBy } from 'lodash';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  type LargeUploadWithS3,
  largeUploadByIdSelector,
  setUploadCompletedAction,
  setUploadFailedAction,
  type Upload,
  type UploadPart,
  uploadedPartsSelector,
  uploadHasUploadInfo,
  useUploaderStore,
} from '@air/redux-uploader';

import { isAbortError } from '../uploadErrors';

export type CompleteLargeUploadParams = {
  uploadId: Upload['id'];
  sendUploadComplete: (upload: LargeUploadWithS3, sortedParts: UploadPart[]) => Promise<void>;
  onUploadFinished: (params: {
    uploadId: Upload['id'];
    uploadUrlType?: Upload['uploadUrlType'];
  }) => Promise<void> | void;
  onError: (params: {
    upload: Upload;
    error: unknown;
    failedStage?: 'complete_upload';
    uploadUrlType?: Upload['uploadUrlType'];
  }) => void;
};

export const useCompleteLargeUpload = () => {
  const store = useUploaderStore();
  const dispatch = useDispatch();

  const completeLargeUpload = useCallback(
    async ({ sendUploadComplete, uploadId, onUploadFinished, onError }: CompleteLargeUploadParams) => {
      const upload = largeUploadByIdSelector(store.getState(), uploadId);

      if (upload && uploadHasUploadInfo(upload)) {
        try {
          const uploadedParts = uploadedPartsSelector(store.getState(), upload.id);
          const sortedParts = sortBy(uploadedParts, 'PartNumber');

          await sendUploadComplete(upload, sortedParts);

          await onUploadFinished({ uploadId, uploadUrlType: upload.uploadUrlType });
          dispatch(setUploadCompletedAction({ uploadId }));
        } catch (error) {
          if (!isAbortError(error)) {
            dispatch(setUploadFailedAction({ uploadId: upload.id }));
          }
          onError({ error, failedStage: 'complete_upload', upload, uploadUrlType: upload.uploadUrlType });
        }
      }
    },
    [dispatch, store],
  );

  return {
    completeLargeUpload,
  };
};
